import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";

export const readOneLicenseTemplate = (data) => {
  const licenseTemplateData = data.licenses.templates.by_id;
  return {
    name: licenseTemplateData.name,
  };
};

export const readLicenseTemplateAccountsMapper = (data) => {
  return data.licenses.templates.by_id.ipmps.map((accountInfo) => ({
    id: Number(accountInfo.id_ipmp),
    name: accountInfo.name,
    description: accountInfo.description,
    enabled: Boolean(accountInfo.enabled),
  }));
};

export const readLicenseTemplateLimitsMapper = (data) => {
  return data.licenses.templates.by_id.limits.map((limitInfo) => ({
    id: Number(limitInfo.id_limit),
    name: limitInfo.limit_type[0].name,
    comment: limitInfo.limit_type[0].description,
    value: limitInfo.value,
    enabled: limitInfo.enable,
  }));
};

export const readLicenseTemplateFeaturesMapper = (data) => {
  const groups = {};
  const features = {};

  const licenseTemplateFeatures = data.licenses.templates.by_id.features;
  licenseTemplateFeatures.forEach((feature) => {
    const [featureType] = get(feature, "feature_type", []);
    if (isEmpty(featureType)) return;
    const [featureGroup] = get(featureType, "feature_groups", []);
    if (isEmpty(featureGroup)) return;

    const groupID = featureGroup.id_feature_group;
    features[groupID] = get(features, groupID, []).concat({
      name: featureType.name,
      comment: featureType.description,
      enabled: !!feature.is_enabled,
      featureId: Number(feature.id_feature),
    });

    if (isEmpty(groups[groupID])) {
      groups[groupID] = {
        name: featureGroup.name,
        description: featureGroup.description,
        groupId: groupID,
      };
    }
  });

  const groupsData = Object.values(groups).sort(
    (a, b) => a.groupId - b.groupId,
  );
  Object.values(features).forEach((featureTypes) =>
    featureTypes.sort((a, b) => a.featureId - b.featureId),
  );

  return [groupsData, features];
};

export const readLicenseTemplateSuggestMapper = (data) => {
  const limits = data.licenses.license_limits
    .map(_getSuggestLimitInfo)
    .filter((limit) => limit);
  const features = data.licenses.license_feature_groups.map(
    _getSuggestFeatureGroupInfo,
  );
  return [limits, features];
};

const _getSuggestLimitInfo = (limit) => {
  if (!limit.enable) return;
  return {
    name: limit.name,
    description: limit.description,
    value: limit.default_value,
    id_limit_type: limit.id_limit_type,
  };
};

const _getSuggestFeatureGroupInfo = (featureGroup) => {
  return {
    name: featureGroup.name,
    features: featureGroup.features.map(_getSuggestFeatureInfo),
  };
};

const _getSuggestFeatureInfo = (feature) => {
  return {
    name: feature.name,
    value: Boolean(feature.is_enabled_by_default),
    id_feature_type: feature.id_feature_type,
  };
};

export const createLicenseTemplateMapper = (data) => {
  const limits = Object.entries(data.limits).map(([id, value]) => ({
    enable: value !== 0,
    id_limit_type: Number(id),
    value,
  }));
  const features = Object.entries(data.features).map(([id, value]) => ({
    id_feature_type: Number(id),
    is_enabled: value,
  }));
  return { ...data, limits, features };
};

export const readFullOneLicenseTemplateMapper = (data) => {
  const licenseFeatures = data.licenses.templates.by_id.features;
  const licenseLimits = data.licenses.templates.by_id.limits;
  const featureTemplates = data.licenses.license_feature_groups;
  const limitsTemplates = data.licenses.license_limits;
  return {
    limits: _fullOneLicenseTemplateLimitsMapper(licenseLimits, limitsTemplates),
    features: _fullOneLicenseTemplateFeaturesMapper(
      licenseFeatures,
      featureTemplates,
    ),
  };
};

const _fullOneLicenseTemplateLimitsMapper = (
  licenseLimits,
  limitsTemplates,
) => {
  return licenseLimits.map((licenseLimit) => ({
    id: licenseLimit.id_limit_type,
    value: licenseLimit.enable ? licenseLimit.value : false,
    name: _fullOneLicenseTemplateLimitMapper(
      limitsTemplates,
      licenseLimit.id_limit_type,
    ),
  }));
};

const _fullOneLicenseTemplateLimitMapper = (limitsTemplates, id_limit_type) => {
  const item = limitsTemplates.find(
    (limitTemplate) => limitTemplate.id_limit_type === id_limit_type,
  );
  if (!item) return "";
  return `${item.description} (${item.name})`;
};

const _fullOneLicenseTemplateFeaturesMapper = (
  licenseFeatures,
  featureGroups,
) => {
  return featureGroups.map((featureGroup) => ({
    name: featureGroup.name,
    features: featureGroup.features.reduce((features, feature) => {
      const licenseFeature = licenseFeatures.find(
        (licenseFeature) =>
          licenseFeature.id_feature_type === feature.id_feature_type,
      );
      if (!licenseFeature) return [...features];
      return {
        ...features,
        [feature.name]: Boolean(licenseFeature.is_enabled),
      };
    }, {}),
  }));
};
